import { FC, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useNavigateTo from 'hooks/useNavigateTo';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { createAnalyticsInteract, getResult, getResultAnswers } from 'store/player/playerAsync';
import { selectQuiz } from 'store/quiz/quizSelectors';
import { selectPreviewPageMode, selectResult } from 'store/player/playerSelectors';
import QuizTypes from 'types/QuizTypes';
import Interactions from 'types/Interactions';
// Components
import PublicDevicePlayable from 'components/PublicDevicePlayable';
import AppBar from 'components/AppBar';
import TopBuckets from './TopBuckets';
import ProceedData from './ProceedData';
import LeaderboardButton from './LeaderboardButton';
import PoweredBy from 'components/PoweredBy';
import { selectResultId } from 'store/auth/authSelectors';

const PostQuizPage:FC = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug:string }>();
  const navigateTo = useNavigateTo();
  // Dispatch
  const dispatch = useAppDispatch();
  
  const result = useAppSelector(selectResult);
  const quiz = useAppSelector(selectQuiz);
  const isPollQuizzes = [QuizTypes.RankingPoll, QuizTypes.Poll].includes(quiz.type);
  const isTeamSelectorQuiz = quiz.type === QuizTypes.TeamSelector;
  const isSurveyQuiz = quiz.type === QuizTypes.Survey;
  const isPersonalityQuiz = quiz.type === QuizTypes.Personality;
  const isSurveyOrPersonalityQuiz = isSurveyQuiz || isPersonalityQuiz;
  const resultId = useAppSelector(selectResultId);
  const previewPageMode = useAppSelector(selectPreviewPageMode);
  
  const handleClick = useCallback((name: Interactions) => {
    if (!resultId) return;
    
    dispatch(createAnalyticsInteract({
      result: resultId,
      name
    }));

    // eslint-disable-next-line
  }, [resultId]);

  useEffect(() => {
    if (previewPageMode) return;
    if (quiz.resultsPage.enabled) {
      if (isPollQuizzes || isTeamSelectorQuiz) {
        dispatch(getResultAnswers({ quizName: slug }));
      } else if (!isSurveyQuiz) {
        dispatch(getResult({ quizName: slug, limit: 96 }));
      }
    } else {
      if (isPersonalityQuiz) dispatch(getResult({ quizName: slug, limit: 96 }));
    }
    if (!quiz.callToAction.enabled && !isSurveyOrPersonalityQuiz) {
      if (isPollQuizzes) {
        navigateTo('results');
      } else if (isTeamSelectorQuiz) {
        navigateTo('teamboard');
      } else {
        navigateTo('leaderboard');
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if ( result && result.quiz ){
      // Set default primary
      document.documentElement.style.setProperty('--primaryColor', result.quiz.theme.primaryColor);
      document.documentElement.style.setProperty('--primaryFontColor', result.quiz.theme.primaryFontColor);
      document.documentElement.style.setProperty('--primaryButtonGradientStart', result.quiz.theme.primaryButtonGradientStart);
      document.documentElement.style.setProperty('--primaryButtonGradientEnd', result.quiz.theme.primaryButtonGradientEnd);
      document.documentElement.style.setProperty('--primaryButtonFontColor', result.quiz.theme.primaryButtonFontColor);
    }
    // eslint-disable-next-line
  }, [result])

  if (!quiz.callToAction.enabled && !isSurveyOrPersonalityQuiz) return null;
  return (
    <div
      className="h-full flex flex-col"
      style={{ background: quiz.backgroundImage ? `url('${quiz.backgroundImage}') top / cover` : '' }}
    >
      <PublicDevicePlayable />
      <div className="p-4"><AppBar /></div>
      <div
        className="flex-1 flex flex-col p-4 overflow-y-auto"
      >
        {isPollQuizzes || isSurveyQuiz ? (
          <div className="h-5/6 px-4 flex flex-col items-center justify-center">
            <h2 className="font-bold text-center text-primary text-3xl mb-8">{quiz.callToAction.headerText}</h2>
            {quiz.callToAction.image ? (
              <a
                className="block rounded-lg mb-4 overflow-hidden border border-white"
                href={quiz.callToAction.url || undefined}
                target="_blank"
                rel="noreferrer"
                onClick={() => handleClick(Interactions.CtaUrlClick)}
              ><img src={quiz.callToAction.image} alt={quiz.callToAction.text || `${t('buttons.visit')}`} /></a>
            ) : null}
            {quiz.callToAction.url ? (
              <div className="text-center">
                <a
                  className="
                    mb-2
                    py-2 px-4
                    inline-block rounded-2xl
                    font-semibold primary-button
                  "
                  href={quiz.callToAction.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => handleClick(Interactions.CtaUrlClick)}

                >{quiz.callToAction.text || t('buttons.visit')}</a>
              </div>
            ) : null}
            {quiz.callToAction.url2 ? (
              <div className="text-center">
                <a
                  className="
                    mb-2
                    py-2 px-4
                    inline-block rounded-2xl
                    font-semibold primary-button
                  "
                  href={quiz.callToAction.url2}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => handleClick(Interactions.CtaUrl2Click)}

                >{quiz.callToAction.text2 || t('buttons.visit')}</a>
              </div>
            ) : null}
          </div>
        ) : null}
        <LeaderboardButton />
        <ProceedData />
        <TopBuckets />
      </div>
      <PoweredBy />
    </div>
  )
}

export default PostQuizPage;
